import React, { useState } from "react";
import { Box, Button, Tooltip, Typography } from "@mui/material";
import { IniInfo, UnconfiguredRules } from "system/types/interfaces";
import { ConfiguredTable } from "./ConfiguredTable";
import { NotConfiguredTable } from "./NotConfiguredTable";
import { AddConfigRuleModal } from "./AddConfigRuleModal";
import { ConfigRuleApplicabilityModal } from "./ConfigRuleApplicabilityModal";
import { InheritedConfigModal } from "../inheritedConfigurationTab/InheritedConfigModal";
import { useSelector } from "react-redux";
import { RootState } from "system/store";
import { normalizeAuthoredConfigValue } from "components/modules/policy-editor/utils/configValueQuoting";
import { toggleCommentRow } from "components/modules/policy-editor/utils/configCommentRows";

interface ConfigurationTabProps {
  configurations: IniInfo[];
  onInsert: (item: IniInfo, position?: number) => void;
  onUpdate: (item: IniInfo) => void;
  onDelete: (id: string) => void;
  onUndoDelete: (id: string) => void;
  onReorder: (oldIndex: number, newIndex: number) => void;
  notConfiguredRules: UnconfiguredRules[];
  readOnlyViewer: boolean;
}

export const ConfigurationTab: React.FC<ConfigurationTabProps> = ({
  configurations,
  onInsert,
  onUpdate,
  onDelete,
  onUndoDelete,
  onReorder,
  notConfiguredRules,
  readOnlyViewer,
}) => {
  const [selectedUnconfigured, setSelectedUnconfigured] = useState<UnconfiguredRules | null>(null);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [insertIndex, setInsertIndex] = useState<number | null>(null);
  const [showApplicabilityModal, setShowApplicabilityModal] = useState(false);
  const [draftConfigRule, setDraftConfigRule] = useState<IniInfo | null>(null);
  const [isInheritedModalOpen, setIsInheritedModalOpen] = useState(false);

  const handleAddRule = (newRule: {
    rule: string;
    applicability: string;
    value: string;
    description: string;
    type: string;
    regex?: string;
    valueShouldBeLogged?: boolean;
  }) => {
    const draft: IniInfo = {
      id: Date.now().toString(),
      name: newRule.rule,
      value: newRule.value,
      applicability: "endpoint",
      delimiter: "is for any",
      pendingChanges: true,
      description: newRule.description,
      type: newRule.type,
      regex: newRule.regex,
      valueShouldBeLogged: newRule.valueShouldBeLogged,
    };

    setDraftConfigRule(draft);
    setIsAddModalOpen(false);

    setSelectedUnconfigured(null);
    setIsAddModalOpen(false);
    setShowApplicabilityModal(true);
  };

  const handleApplicabilityConfirmed = (finalRule: IniInfo) => {
    finalRule.value =
      normalizeAuthoredConfigValue(finalRule.name ?? "", finalRule.value, finalRule.delimiter ?? "", finalRule.applicability ?? "") ??
      finalRule.value;

    const existingIndex = configurations.findIndex((x) => x.id === finalRule.id);

    if (existingIndex >= 0) {
      onUpdate(finalRule);
    } else {
      onInsert(finalRule, insertIndex ?? undefined);
    }

    setInsertIndex(null);
    setShowApplicabilityModal(false);
    setDraftConfigRule(null);
  };

  const handleToggleComment = (id: string, isComment: boolean) => {
    const row = configurations.find((c) => c.id === id);
    if (!row) return;
    const toggled = toggleCommentRow(row, isComment);
    if (toggled) onUpdate(toggled);
  };

  const handleOpenEditModal = (rule: IniInfo) => {
    setDraftConfigRule({ ...rule, value: rule.value.trim() });
    setShowApplicabilityModal(true);
  };

  return (
    <Box>
      {!readOnlyViewer && (
        <Box sx={{ display: "flex", justifyContent: "flex-start", mt: 2, mb: 3 }}>
          <Tooltip placement="right" title="Add configurations that apply to multiple packages via regex inheritance.">
            <Button variant="contained" onClick={() => setIsInheritedModalOpen(true)}>
              Add Inherited Configurations
            </Button>
          </Tooltip>
        </Box>
      )}
      <Typography variant="h6" mb={2} sx={{ color: "#fff" }}>
        Configured Rules
      </Typography>

      <ConfiguredTable
        configuredRules={configurations}
        onAdd={(index: number) => {
          setInsertIndex(index);
          setIsAddModalOpen(true);
        }}
        onDelete={onDelete}
        onUndoDelete={onUndoDelete}
        onEdit={handleOpenEditModal}
        onToggleComment={handleToggleComment}
        onReorder={onReorder}
        readOnlyViewer={readOnlyViewer}
      />

      <Typography variant="h6" sx={{ color: "#fff", mt: 3, mb: 1 }}>
        Default Configurations
      </Typography>
      <NotConfiguredTable
        notConfiguredRules={notConfiguredRules}
        readOnlyViewer={readOnlyViewer}
        onAdd={(ruleName) => {
          const found = notConfiguredRules.find((x) => x.name === ruleName);
          if (!found) return;

          setSelectedUnconfigured(found);
          setIsAddModalOpen(true);
        }}
      />
      <AddConfigRuleModal
        open={isAddModalOpen}
        onClose={() => {
          setSelectedUnconfigured(null);
          setIsAddModalOpen(false);
        }}
        onAddRule={handleAddRule}
        unConfiguredList={notConfiguredRules}
        initialItem={selectedUnconfigured}
      />
      <InheritedConfigModal
        open={isInheritedModalOpen}
        onClose={() => setIsInheritedModalOpen(false)}
        subjectName={useSelector((state: RootState) => state.policyEditor.selectedPackage?.rule?.name || "")}
      />
      {draftConfigRule && (
        <ConfigRuleApplicabilityModal
          open={showApplicabilityModal}
          onClose={() => {
            setShowApplicabilityModal(false);
            setDraftConfigRule(null);
          }}
          initialRule={draftConfigRule}
          onFinish={(finalRule) => {
            handleApplicabilityConfirmed(finalRule);
          }}
        />
      )}
    </Box>
  );
};
